class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector("input");
    this.changeEvent = new Event("change", { bubbles: true });

    this.input.addEventListener("change", this.onInputChange.bind(this));
    this.querySelectorAll("button").forEach((button) =>
      button.addEventListener("click", this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(
      PUB_SUB_EVENTS.quantityUpdate,
      this.validateQtyRules.bind(this)
    );
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    event.target.name === "plus" ? this.input.stepUp() : this.input.stepDown();
    if (previousValue !== this.input.value)
      this.input.dispatchEvent(this.changeEvent);
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const min = parseInt(this.input.min);
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle("disabled", value <= min);
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle("disabled", value >= max);
    }
  }
}
customElements.define("quantity-input", QuantityInput);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
    this.addEventListener("change", this.onVariantChange);
  }

  onVariantChange() {
    this.updateOptions();

    this.updateMasterId();
    this.toggleAddButton(true, "", false);
    this.updatePickupAvailability();
    this.removeErrorMessage();
    this.updateVariantStatuses();

    if (!this.currentVariant) {
      this.toggleAddButton(true, "", true);
      this.setUnavailable();
    } else {
      this.updateMedia();
      this.updateURL();
      this.updateVariantInput();
      this.renderProductInfo();
      this.updateShareUrl();
    }
  }

  updateOptions() {
    this.options = Array.from(
      this.querySelectorAll("select"),
      (select) => select.value
    );
  }

  updateMasterId() {
    this.currentVariant = this.getVariantData().find((variant) => {
      return !variant.options
        .map((option, index) => {
          return this.options[index] === option;
        })
        .includes(false);
    });
  }

  updateMedia() {
    if (!this.currentVariant) return;

    const isMiniPdp = this.dataset.context === "mini-pdp";

    const displaySectionId = isMiniPdp
      ? this.dataset.productSection
      : this.dataset.section;
    const productGallery = document.getElementById(
      `product-gallery-${displaySectionId}`
    );

    if (productGallery) productGallery.refresh(this.currentVariant);
  }

  updateURL() {
    if (!this.currentVariant || this.dataset.updateUrl === "false") return;
    window.history.replaceState(
      {},
      "",
      `${this.dataset.url}?variant=${this.currentVariant.id}`
    );
  }

  updateShareUrl() {
    const shareButton = document.getElementById(
      `Share-${this.dataset.section}`
    );
    if (!shareButton || !shareButton.updateUrl) return;
    shareButton.updateUrl(
      `${window.shopUrl}${this.dataset.url}?variant=${this.currentVariant.id}`
    );
  }

  updateVariantInput() {
    const isMiniPdp = this.dataset.context === "mini-pdp";
    const displaySectionId = isMiniPdp
      ? this.dataset.productSection
      : this.dataset.section;

    const productForms = document.querySelectorAll(
      `#product-form-${displaySectionId}, #product-form-installment-${displaySectionId}`
    );
    productForms.forEach((productForm) => {
      const input = productForm.querySelector('input[name="id"]');
      input.value = this.currentVariant.id;
      input.dispatchEvent(new Event("change", { bubbles: true }));
    });
  }

  updateVariantStatuses() {
    const selectedOptionOneVariants = this.variantData.filter(
      (variant) => this.querySelector(":checked").value === variant.option1
    );
    const inputWrappers = [...this.querySelectorAll(".product-form__input")];
    inputWrappers.forEach((option, index) => {
      if (index === 0) return;
      const optionInputs = [
        ...option.querySelectorAll('input[type="radio"], option'),
      ];
      const previousOptionSelected =
        inputWrappers[index - 1].querySelector(":checked").value;
      const availableOptionInputsValue = selectedOptionOneVariants
        .filter(
          (variant) =>
            variant.available &&
            variant[`option${index}`] === previousOptionSelected
        )
        .map((variantOption) => variantOption[`option${index + 1}`]);
      this.setInputAvailability(optionInputs, availableOptionInputsValue);
    });
  }

  setInputAvailability(listOfOptions, listOfAvailableOptions) {
    listOfOptions.forEach((input) => {
      if (listOfAvailableOptions.includes(input.getAttribute("value"))) {
        input.innerText = input.getAttribute("value");
      } else {
        input.innerText = window.variantStrings.unavailable_with_option.replace(
          "[value]",
          input.getAttribute("value")
        );
      }
    });
  }

  updatePickupAvailability() {
    const pickUpAvailability = document.querySelector("pickup-availability");
    if (!pickUpAvailability) return;
    if (this.currentVariant && this.currentVariant.available) {
      pickUpAvailability.fetchAvailability(this.currentVariant.id);
    } else {
      pickUpAvailability.removeAttribute("available");
      pickUpAvailability.innerHTML = "";
    }
  }

  removeErrorMessage() {
    const section = this.closest("section");
    if (!section) return;

    const productForm = section.querySelector("product-form");
    if (productForm) productForm.handleErrorMessage();
  }

  renderProductInfo() {
    const isMiniPdp = this.dataset.context === "mini-pdp";
    const requestedVariantId = this.currentVariant.id;
    const sectionId = this.dataset.originalSection
      ? this.dataset.originalSection
      : this.dataset.section;

    fetch(
      `${this.dataset.url}?variant=${requestedVariantId}&section_id=${
        this.dataset.originalSection
          ? this.dataset.originalSection
          : this.dataset.section
      }`
    )
      .then((response) => response.text())
      .then((responseText) => {
        // prevent unnecessary ui changes from abandoned selections
        if (this.currentVariant.id !== requestedVariantId) return;

        const html = new DOMParser().parseFromString(responseText, "text/html");

        const displaySectionId = isMiniPdp
          ? this.dataset.productSection
          : sectionId;

        const destination = document.getElementById(
          `price-${displaySectionId}`
        );

        const source = html.getElementById(`price-${displaySectionId}`);
        const skuSource = html.getElementById(`Sku-${displaySectionId}`);
        const skuDestination = document.getElementById(
          `Sku-${displaySectionId}`
        );
        const inventorySource = html.getElementById(
          `Inventory-${displaySectionId}`
        );
        const inventoryDestination = document.getElementById(
          `Inventory-${displaySectionId}`
        );
        const unitPriceInuptSource = html.getElementById(
          `unit-price-${displaySectionId}`
        );

        const unitPriceInuptdest = document.getElementById(
          `unit-price-${displaySectionId}`
        );

        if (source && destination) destination.innerHTML = source.innerHTML;
        if (inventorySource && inventoryDestination)
          inventoryDestination.innerHTML = inventorySource.innerHTML;
        if (skuSource && skuDestination) {
          skuDestination.innerHTML = skuSource.innerHTML;
          skuDestination.classList.toggle(
            "visibility-hidden",
            skuSource.classList.contains("visibility-hidden")
          );
        }

        if (unitPriceInuptSource && unitPriceInuptdest) {
          unitPriceInuptdest.outerHTML = unitPriceInuptSource.outerHTML;
        }

        const price = document.getElementById(`price-${displaySectionId}`);

        if (price) price.classList.remove("visibility-hidden");

        if (source.dataset.available === "false") {
          document
            .querySelectorAll(".prdouct-price--unavailable-variant")
            .forEach((el) => {
              el.classList.remove("hidden");
            });
          document
            .querySelectorAll(".product-price--available-variant")
            .forEach((el) => {
              el.classList.add("hidden");
            });
        } else {
          document
            .querySelectorAll(".prdouct-price--unavailable-variant")
            .forEach((el) => {
              el.classList.add("hidden");
            });
          document
            .querySelectorAll(".product-price--available-variant")
            .forEach((el) => {
              el.classList.remove("hidden");
            });
        }

        if (inventoryDestination)
          inventoryDestination.classList.toggle(
            "visibility-hidden",
            inventorySource.innerText === ""
          );

        const description_destination = document.getElementById(
          `product-description-${displaySectionId}`
        );
        const description_source = html.getElementById(
          `product-description-${displaySectionId}`
        );
        if (description_source && description_destination)
          description_destination.innerHTML = description_source.innerHTML;

        const thumbnail_destination = document.getElementById(
          `product-thumbnail-${displaySectionId}`
        );
        const thumbnail_source = html.getElementById(
          `product-thumbnail-${displaySectionId}`
        );
        if (thumbnail_source && thumbnail_destination)
          thumbnail_destination.innerHTML = thumbnail_source.innerHTML;

        const subscription_destination = document.getElementById(
          `product-subscription-modal-content-${this.dataset.section}`
        );
        const subscription_source = html.getElementById(
          `product-subscription-modal-content-${displaySectionId}`
        );
        if (subscription_source && subscription_destination)
          subscription_destination.innerHTML = subscription_source.innerHTML;
        const addButtonUpdated = html.getElementById(
          `ProductSubmitButton-${displaySectionId}`
        );

        this.toggleAddButton(
          addButtonUpdated
            ? addButtonUpdated.dataset.available === "false"
            : true,
          window.variantStrings.soldOut
        );
        const quickAddModal = this.closest("quick-add-modal");
        if (quickAddModal) {
          const modalButon = html.getElementById(
            `product-bis-modal-opener-${displaySectionId}`
          );

          this.updateBisForm(this.currentVariant.id);
          this.toggleBisButton(
            addButtonUpdated
              ? addButtonUpdated.dataset.available === "false"
              : true
          );
        } else {
          this.updateBisForm(this.currentVariant.id);
          this.toggleBisButton(
            addButtonUpdated
              ? addButtonUpdated.dataset.available === "false"
              : true
          );
        }

        publish(PUB_SUB_EVENTS.variantChange, {
          data: {
            sectionId,
            html,
            variant: this.currentVariant,
          },
        });
      });
  }

  toggleAddButton(disable = true, text, modifyClass = true) {
    const isMiniPdp = this.dataset.context === "mini-pdp";
    const displaySectionId = isMiniPdp
      ? this.dataset.productSection
      : this.dataset.section;

    const productForm = document.getElementById(
      `product-form-${displaySectionId}`
    );

    if (!productForm) return;
    const addButton = productForm.querySelector('[name="add"]');
    const addButtonText = productForm.querySelector('[name="add"] > span');
    if (!addButton) return;
    if (disable) {
      addButton.setAttribute("disabled", "disabled");
      if (text) addButtonText.textContent = text;
    } else {
      addButton.removeAttribute("disabled");
      addButtonText.textContent = window.variantStrings.addToCart;
    }

    if (!modifyClass) return;
  }

  toggleBisButton(hide = true) {
    const isMiniPdp = this.dataset.context === "mini-pdp";
    const displaySectionId = isMiniPdp
      ? this.dataset.productSection
      : this.dataset.section;

    const productForm = document.getElementById(
      `product-form-${displaySectionId}`
    );

    if (!productForm) return;
    const addButton = productForm.querySelector('[name="add"]');
    const BisButton = document.getElementById(
      `product-bis-modal-opener-${displaySectionId}`
    );
    if (hide) {
      addButton.classList.add("hidden");
      BisButton.classList.remove("hidden");
    } else {
      addButton.classList.remove("hidden");
      BisButton.classList.add("hidden");
    }
  }

  updateBisForm(variantId) {
    const bisForm = document.getElementById("pdp-bis-modal-form");
    if (bisForm)
      bisForm.querySelector('input[name="bis_variant_id"]').value = variantId;
  }

  setUnavailable() {
    const button = document.getElementById(
      `product-form-${this.dataset.section}`
    );
    const addButton = button.querySelector('[name="add"]');
    const addButtonText = button.querySelector('[name="add"] > span');
    const price = document.getElementById(`price-${this.dataset.section}`);
    const inventory = document.getElementById(
      `Inventory-${this.dataset.section}`
    );
    const sku = document.getElementById(`Sku-${this.dataset.section}`);

    if (!addButton) return;
    addButtonText.textContent = window.variantStrings.unavailable;
    if (price) price.classList.add("visibility-hidden");
    if (inventory) inventory.classList.add("visibility-hidden");
    if (sku) sku.classList.add("visibility-hidden");
  }

  getVariantData() {
    this.variantData =
      this.variantData ||
      JSON.parse(this.querySelector('[type="application/json"]').textContent);
    return this.variantData;
  }
}
customElements.define("variant-selects", VariantSelects);

class VariantRadios extends VariantSelects {
  constructor() {
    super();
  }

  setInputAvailability(listOfOptions, listOfAvailableOptions) {
    listOfOptions.forEach((input) => {
      if (listOfAvailableOptions.includes(input.getAttribute("value"))) {
        input.classList.remove("disabled");
      } else {
        input.classList.add("disabled");
      }
    });
  }

  updateOptions() {
    const fieldsets = Array.from(this.querySelectorAll("fieldset"));
    this.options = fieldsets.map((fieldset) => {
      return Array.from(fieldset.querySelectorAll("input")).find(
        (radio) => radio.checked
      ).value;
    });

    // Updated to set the max quantity for the selected variant in Quick add modal
    if (!this.closest("quick-add-modal")) return;

    const qty_max_num = Array.from(fieldsets)
      .flatMap((fieldset) => Array.from(fieldset.querySelectorAll("input")))
      .find((radio) => radio.checked)?.dataset.quantityMax;

    if (!qty_max_num) return;

    this.closest("product-form")
      .querySelector(".quantity__input")
      .setAttribute("max", qty_max_num);
    this.closest("product-form").querySelector(".quantity__input").dataset.max =
      qty_max_num;
    this.closest("product-form").querySelector(".quantity__input").value = 1;

    // console.log("qty_max_num", this.closest("quick-add-modal"), qty_max_num, this.closest("product-form").querySelector(".quantity__input"), this.closest("product-form").querySelector(".quantity__input").value);
  }
}
customElements.define("variant-radios", VariantRadios);

class ProductInfo extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector(".quantity__input");
    this.currentVariant = this.querySelector(".product-variant-id");
    this.variantSelects = this.querySelector("variant-radios");
    this.submitButton = this.querySelector('[type="submit"]');
  }

  cartUpdateUnsubscriber = undefined;
  variantChangeUnsubscriber = undefined;

  connectedCallback() {
    if (!this.input) return;
    this.quantityForm = this.querySelector(".product-form__quantity");
    if (!this.quantityForm) return;
    this.setQuantityBoundries();
    if (!this.dataset.originalSection) {
      this.cartUpdateUnsubscriber = subscribe(
        PUB_SUB_EVENTS.cartUpdate,
        this.fetchQuantityRules.bind(this)
      );
    }
    this.variantChangeUnsubscriber = subscribe(
      PUB_SUB_EVENTS.variantChange,
      (event) => {
        const sectionId = this.dataset.originalSection
          ? this.dataset.originalSection
          : this.dataset.section;
        if (event.data.sectionId !== sectionId) return;
        this.updateQuantityRules(event.data.sectionId, event.data.html);
        this.setQuantityBoundries();
      }
    );
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
    if (this.variantChangeUnsubscriber) {
      this.variantChangeUnsubscriber();
    }
  }

  setQuantityBoundries() {
    const data = {
      cartQuantity: this.input.dataset.cartQuantity
        ? parseInt(this.input.dataset.cartQuantity)
        : 0,
      min: this.input.dataset.min ? parseInt(this.input.dataset.min) : 1,
      max: this.input.dataset.max ? parseInt(this.input.dataset.max) : null,
      step: this.input.step ? parseInt(this.input.step) : 1,
    };

    let min = data.min;
    const max = data.max === null ? data.max : data.max - data.cartQuantity;
    if (max !== null) min = Math.min(min, max);
    if (data.cartQuantity >= data.min) min = Math.min(min, data.step);
    this.input.min = min;
    this.input.max = max;
    this.input.value = min;
    publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
  }

  fetchQuantityRules() {
    if (!this.currentVariant || !this.currentVariant.value) return;
    this.querySelector(
      ".quantity__rules-cart .loading-overlay"
    ).classList.remove("hidden");
    fetch(
      `${this.dataset.url}?variant=${
        this.currentVariant.value
      }&section_id=${this.dataset.section.replace("quickadd-", "")}`
    )
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, "text/html");
        this.updateQuantityRules(
          this.dataset.section.replace("quickadd-", ""),
          html
        );
        this.setQuantityBoundries();
      })
      .catch((e) => {
        console.error(e);
      })
      .finally(() => {
        this.querySelector(
          ".quantity__rules-cart .loading-overlay"
        ).classList.add("hidden");
      });
  }

  updateQuantityRules(sectionId, html) {
    const quantityFormUpdated = html.getElementById(
      `Quantity-Form-${sectionId}`
    );
    const selectors = [
      ".quantity__input",
      ".quantity__rules",
      ".quantity__label",
    ];
    for (let selector of selectors) {
      const current = this.quantityForm.querySelector(selector);
      const updated = quantityFormUpdated.querySelector(selector);
      if (!current || !updated) continue;
      if (selector === ".quantity__input") {
        const attributes = [
          "data-cart-quantity",
          "data-min",
          "data-max",
          "step",
        ];
        for (let attribute of attributes) {
          const valueUpdated = updated.getAttribute(attribute);
          if (valueUpdated !== null)
            current.setAttribute(attribute, valueUpdated);
        }
      } else {
        current.innerHTML = updated.innerHTML;
      }
    }
  }
}
customElements.define("product-info", ProductInfo);

class ProductForm extends HTMLElement {
  constructor() {
    super();

    this.form = this.querySelector("form");
    this.form.querySelector("[name=id]").disabled = false;
    this.form.addEventListener("submit", this.onSubmitHandler.bind(this));
    this.cart = document.querySelector("cart-drawer");
    this.submitButton = this.querySelector('[type="submit"]');
    if (document.querySelector("cart-drawer"))
      this.submitButton?.setAttribute("aria-haspopup", "dialog");

    this.hideErrors = this.dataset.hideErrors === "true";
  }

  onSubmitHandler(evt) {
    evt.preventDefault();
    if (this.submitButton.getAttribute("aria-disabled") === "true") return;

    this.handleErrorMessage();

    // this.submitButton.setAttribute("aria-disabled", true);
    this.submitButton.classList.add("loading");
    this.querySelector(".loading__spinner").classList.remove("hidden");

    const config = fetchConfig("javascript");
    config.headers["X-Requested-With"] = "XMLHttpRequest";
    delete config.headers["Content-Type"];

    const formData = new FormData(this.form);
    if (this.cart) {
      formData.append(
        "sections",
        this.cart.getSectionsToRender().map((section) => section.section)
      );
      formData.append("sections_url", window.location.pathname);
      this.cart.setActiveElement(document.activeElement);
    }
    config.body = formData;
    fetch(`${routes.cart_add_url}`, config)
      .then((response) => response.json())
      .then((response) => {
        if (window.gwp.isGWPAvailable()) {
          return window.gwp.getGWPCart().then((cart) => {
            if (!window.gwp.isGWPItemInCart(cart) && cart.item_count <= 1) {
              return window.gwp.addGWPToCart(
                this.cart
                  .getSectionsToRender()
                  .map((section) => section.section)
              );
            } else {
              return response;
            }
          });
        } else {
          return response;
        }
      })
      .then((response) => {
        if (response.status) {
          publish(PUB_SUB_EVENTS.cartError, {
            source: "product-form",
            productVariantId: formData.get("id"),
            errors: response.errors || response.description,
            message: response.message,
          });
          this.handleErrorMessage(response.description);

          const soldOutMessage =
            this.submitButton.querySelector(".sold-out-message");
          if (!soldOutMessage) return;
          this.submitButton.setAttribute("aria-disabled", true);
          this.submitButtonText.classList.add("hidden");
          soldOutMessage.classList.remove("hidden");
          this.error = true;
          return;
        } else if (!this.cart) {
          window.location = window.routes.cart_url;
          return;
        }

        if (!this.error)
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: "product-form",
            productVariantId: formData.get("id"),
            cartData: response,
          });
        this.error = false;
        const quickAddModal = this.closest("quick-add-modal");
        if (quickAddModal) {
          document.body.addEventListener(
            "modalClosed",
            () => {
              setTimeout(() => {
                this.cart.renderContents(response);
              });
            },
            { once: true }
          );
          quickAddModal.hide(true);
        } else {
          this.cart.renderContents(response);
        }
        this.announce(this.submitButton.dataset.accessibility);
      })
      .catch((e) => {
        console.error(e);
      })
      .finally(() => {
        this.submitButton.classList.remove("loading");
        if (this.cart && this.cart.classList.contains("is-empty"))
          this.cart.classList.remove("is-empty");
        if (!this.error) this.submitButton.removeAttribute("aria-disabled");
        this.querySelector(".loading__spinner").classList.add("hidden");
      });
  }

  handleErrorMessage(errorMessage = false) {
    if (this.hideErrors) return;

    this.errorMessage =
      this.errorMessage || this.querySelector(".product-form__error-message");
    if (!this.errorMessage) return;

    this.errorMessage.classList.toggle("active", errorMessage);

    if (errorMessage) {
      this.errorMessage.textContent = errorMessage;
    }
  }

  announce(message) {
    const region = document.getElementById("aria-live-region");
    if (region) {
      region.textContent = message;
      setTimeout(() => {
        region.textContent = "";
      }, 1000);
    }
  }
}
customElements.define("product-form", ProductForm);

class QuickAddModal extends ModalDialog {
  constructor() {
    super();
    this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');
  }

  hide(preventFocus = false) {
    const cartNotification = document.querySelector("cart-drawer");
    if (cartNotification) cartNotification.setActiveElement(this.openedBy);
    this.modalContent.innerHTML = "";

    if (preventFocus) this.openedBy = null;
    super.hide();
  }

  show(opener) {
    // opener.setAttribute("aria-disabled", true);
    opener.classList.add("loading");
    opener.querySelector(".loading__spinner").classList.remove("hidden");

    fetch(opener.getAttribute("data-product-url"))
      .then((response) => response.text())
      .then((responseText) => {
        const responseHTML = new DOMParser().parseFromString(
          responseText,
          "text/html"
        );
        this.productElement = responseHTML.querySelector(
          'section[id^="MainProduct-"]'
        );
        this.preventDuplicatedIDs();
        this.removeDOMElements();
        this.setInnerHTML(this.modalContent, this.productElement.innerHTML);

        if (window.Shopify && Shopify.PaymentButton) {
          Shopify.PaymentButton.init();
        }

        if (window.ProductModel) window.ProductModel.loadShopifyXR();

        this.preventVariantURLSwitching();

        setTimeout(() => {
          super.show(opener);
        }, 200);
      })
      .finally(() => {
        // opener.removeAttribute("aria-disabled");
        opener.classList.remove("loading");
        opener.querySelector(".loading__spinner").classList.add("hidden");
      });
  }

  setInnerHTML(element, html) {
    element.innerHTML = html;

    // Reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
    element.querySelectorAll("script").forEach((oldScriptTag) => {
      const newScriptTag = document.createElement("script");
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }

  preventVariantURLSwitching() {
    this.modalContent
      .querySelector("variant-radios,variant-selects")
      .setAttribute("data-update-url", "false");
  }

  removeDOMElements() {
    const pickupAvailability = this.productElement.querySelector(
      "pickup-availability"
    );
    if (pickupAvailability) pickupAvailability.remove();

    const productModal = this.productElement.querySelector("product-modal");
    if (productModal) productModal.remove();
  }

  preventDuplicatedIDs() {
    const sectionId = this.productElement.dataset.section;
    this.productElement.innerHTML = this.productElement.innerHTML.replaceAll(
      sectionId,
      `quickadd-${sectionId}`
    );
    this.productElement
      .querySelectorAll("variant-selects, variant-radios")
      .forEach((variantSelect) => {
        variantSelect.dataset.originalSection = sectionId;
      });
  }
}
customElements.define("quick-add-modal", QuickAddModal);

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener(
      "keyup",
      (event) => event.code === "Escape" && this.close()
    );

    this.addEventListener("click", this.onModalClick.bind(this));

    this.setHeaderCartIconAccessibility();

    window.addEventListener("pageshow", this.onPageShow.bind(this));
  }

  onPageShow(event) {
    if (
      event.persisted ||
      performance.getEntriesByType("navigation")[0].type === "back_forward"
    ) {
      //location.reload();
      this.syncSections();
    }
  }

  syncSections() {
    fetch(
      `${window.location.pathname}?sections=${this.getSectionsToRender()
        .map((section) => section.section)
        .toString()}`,
      { cache: "no-cache" }
    )
      .then((response) => response.json())
      .then((response) => {
        this.getSectionsToRender().forEach((section) => {
          const elementToReplace =
            document
              .getElementById(section.id)
              ?.querySelector(section.selector) ||
            document.getElementById(section.id);
          if (elementToReplace)
            elementToReplace.innerHTML = this.getSectionInnerHTML(
              response[section.section],
              section.selector
            );
        });
      })
      .catch((e) => {
        console.error(e);
      });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector("#cart-bubble");
    cartLink.setAttribute("role", "button");
    cartLink.setAttribute("aria-haspopup", "dialog");
    cartLink.addEventListener("click", (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener("keydown", (event) => {
      if (event.code.toUpperCase() === "SPACE") {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  onModalClick(event) {
    if (event.target === this) this.close();
    if (event.target.closest("#CartDrawer-Close")) this.close();
  }

  open(opener) {
    if (opener) this.setActiveElement(opener);
    this.openBy = opener;
    this.setAttribute("open", "");
    trapFocus(this, this.querySelector('[role="dialog"]'));
    document.body.classList.add("cart-drawer-opening", "overflow-hidden");
  }

  close() {
    removeTrapFocus(this.openBy);
    this.removeAttribute("open");
    document.body.classList.remove("cart-drawer-opening", "overflow-hidden");
  }

  renderContents(parsedState) {
    this.getSectionsToRender().forEach((section) => {
      const elementToReplace =
        document.getElementById(section.id)?.querySelector(section.selector) ||
        document.getElementById(section.id);
      if (elementToReplace)
        elementToReplace.innerHTML = this.getSectionInnerHTML(
          parsedState.sections[section.section],
          section.selector
        );
    });

    if (themeTemplate != "cart") {
      setTimeout(() => {
        this.open();
      });
    }
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: "CartDrawer",
        section: "cart-drawer",
        selector: ".cart-drawer-content",
      },
      {
        id: "cart-bubble",
        section: "cart-bubble",
        selector: ".shopify-section",
      },
      {
        id: "Cart",
        section: "cart",
        selector: ".cart-content",
      },
    ];
  }

  getSectionDOM(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}
customElements.define("cart-drawer", CartDrawer);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById("shopping-cart-line-item-status") ||
      document.getElementById("CartDrawer-LineItemStatus");

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener("change", debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(
      PUB_SUB_EVENTS.cartUpdate,
      (event) => {
        if (event.source === "cart-items") {
          return;
        }
        this.onCartUpdate();
      }
    );
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  onChange(event) {
    if (event.target.hasAttribute("data-quantity")) {
      this.updateLineItem({
        line: event.target.dataset.index,
        quantity: event.target.value,
        name: document.activeElement.getAttribute("name"),
        eventTarget: event.target,
      });
    } else if (event.target.hasAttribute("data-selling-plan")) {
      this.updateLineItem({
        line: event.target.dataset.index,
        quantity: 1,
        sellingPlan: event.target.value,
        name: document.activeElement.getAttribute("name"),
        eventTarget: event.target,
      });
    } else {
    }
  }

  onCartUpdate() {
    fetch(`${routes.cart_url}?section_id=cart`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, "text/html");
        const sourceQty = html.querySelector("cart-items");
        this.innerHTML = sourceQty.innerHTML;
      })
      .catch((e) => {
        console.error(e);
      });
  }

  getSectionsToRender() {
    return [
      {
        id: "CartDrawer",
        section: "cart-drawer",
        selector: ".cart-drawer-content",
      },
      {
        id: "cart-bubble",
        section: "cart-bubble",
        selector: ".shopify-section",
      },
      {
        id: "Cart",
        section: "cart",
        selector: ".cart-content",
      },
    ];
  }

  updateLineItem({ line, quantity, sellingPlan, name, eventTarget }) {
    this.enableLoading(line);

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      selling_plan: sellingPlan,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);
        if (
          window.gwp.isGWPItemInCart(parsedState) &&
          !window.gwp.isNonGWPItemInCart(parsedState)
        ) {
          return window.gwp.clearCart(
            parsedState,
            this.getSectionsToRender().map((section) => section.section)
          );
        } else if (
          // Remove GWP item Y from cart if no discount is applied
          window.gwp.isGWPItemYInCart(parsedState)
        ) {
          return window.gwp.removeGWPfromCart(
            parsedState,
            this.getSectionsToRender().map((section) => section.section)
          );
        } else {
          return state;
        }
      })
      .then((state) => {
        const parsedState = JSON.parse(state);

        const quantityElement = eventTarget.id.startsWith("CartDrawer")
          ? document.getElementById(`CartDrawer-Quantity-${line}`)
          : document.getElementById(`Cart-Quantity-${line}`);

        const items = eventTarget.id.startsWith("CartDrawer")
          ? document.querySelectorAll(".cart-drawer-item")
          : document.querySelectorAll(".cart-item");

        if (parsedState.errors) {
          quantityElement.value = quantityElement.getAttribute("value");
          this.updateLiveRegions(line, parsedState.errors, input);
          return;
        }

        const cartDrawerWrapper = document.querySelector("cart-drawer");
        this.getSectionsToRender().forEach((section) => {
          const elementToReplace =
            document
              .getElementById(section.id)
              ?.querySelector(section.selector) ||
            document.getElementById(section.id);
          if (elementToReplace)
            elementToReplace.innerHTML = this.getSectionInnerHTML(
              parsedState.sections[section.section],
              section.selector
            );
        });

        const updatedValue = parsedState.items[line - 1]
          ? parsedState.items[line - 1].quantity
          : undefined;
        let message = "";
        if (
          items.length === parsedState.items.length &&
          updatedValue !== parseInt(quantityElement.value)
        ) {
          if (typeof updatedValue === "undefined") {
            message = window.cartStrings.error;
          } else {
            message = window.cartStrings.quantityError.replace(
              "[quantity]",
              updatedValue
            );
          }
        }
        this.updateLiveRegions(line, message, eventTarget);

        const lineItem = eventTarget.id.startsWith("CartDrawer")
          ? document.getElementById(`CartDrawer-Item-${line}`)
          : document.getElementById(`Cart-Item-${line}`);

        if (parsedState.item_count === 0) {
          eventTarget.id.startsWith("CartDrawer")
            ? trapFocus(
                cartDrawerWrapper.querySelector(".cart-drawer-empty"),
                cartDrawerWrapper.querySelector("a")
              )
            : document.querySelector(".cart-empty a").focus();
        } else if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
          eventTarget.id.startsWith("CartDrawer")
            ? trapFocus(
                cartDrawerWrapper,
                lineItem.querySelector(`[name="${name}"]`)
              )
            : lineItem.querySelector(`[name="${name}"]`).focus();
        } else {
          eventTarget.id.startsWith("CartDrawer")
            ? trapFocus(
                cartDrawerWrapper,
                document.querySelector(".cart-drawer-item-link")
              )
            : document.querySelector(".cart-item-link").focus();
        }

        publish(PUB_SUB_EVENTS.cartUpdate, { source: "cart-items" });
      })
      .catch((e) => {
        // console.log(e);
        this.querySelectorAll(".loading-overlay").forEach((overlay) =>
          overlay.classList.add("hidden")
        );
        const cartErrors = eventTarget.id.startsWith("CartDrawer")
          ? document.getElementById("CartDrawer-Errors")
          : document.getElementById("Cart-Errors");
        cartErrors.textContent = window.cartStrings.error;

        // If customer is available then first logout for handling Oribe App rule
        // ShopifyAnalytics.meta.page.customerId is the way to find out the customer but it is only working on pageload
        if (document.body.getAttribute("data-customer")) {
          fetch(`${routes.account_logout_url}`)
            .then((response) => response.text())
            .then((response) => {
              document.body.removeAttribute("data-customer");
              Cookies.set("logged_in", "false", {
                expires: 0,
              });
              this.updateLineItem({
                line: line,
                quantity: quantity,
                name: name,
                eventTarget: eventTarget,
              });
            })
            .catch((e) => {
              console.error(e);
            })
            .finally(() => {});
        }
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  updateLiveRegions(line, message, eventTarget) {
    const lineItemError = eventTarget.id.startsWith("CartDrawer")
      ? document.getElementById(`CartDrawer-LineItem-Error-${line}`)
      : document.getElementById(`Cart-Lineitem-Error-${line}`);
    if (lineItemError)
      lineItemError.querySelector(".cart-item__error-text").innerHTML = message;

    this.lineItemStatusElement.setAttribute("aria-hidden", true);

    const cartStatus =
      document.getElementById("cart-live-region-text") ||
      document.getElementById("CartDrawer-LiveRegionText");
    cartStatus.setAttribute("aria-hidden", false);

    setTimeout(() => {
      cartStatus.setAttribute("aria-hidden", true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const cart = document.getElementById("Cart");
    const cartDrawer = document.getElementById("CartDrawer");

    if (cart) {
      cart.classList.add("cart__items--disabled");
    }
    if (cartDrawer) {
      cartDrawer.classList.add("cart__items--disabled");
    }

    const cartItemElements = this.querySelectorAll(
      `#Cart-Item-${line} .loading-overlay`
    );
    const cartDrawerItemElements = this.querySelectorAll(
      `#CartDrawer-Item-${line} .loading-overlay`
    );

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) =>
      overlay.classList.remove("hidden")
    );

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute("aria-hidden", false);
  }

  disableLoading(line) {
    const cart = document.getElementById("Cart");
    const cartDrawer = document.getElementById("CartDrawer");

    if (cart) {
      cart.classList.remove("cart__items--disabled");
    }
    if (cartDrawer) {
      cartDrawer.classList.remove("cart__items--disabled");
    }

    const cartItemElements = this.querySelectorAll(
      `#Cart-Item-${line} .loading-overlay`
    );
    const cartDrawerItemElements = this.querySelectorAll(
      `#CartDrawer-Item-${line} .loading-overlay`
    );

    cartItemElements.forEach((overlay) => overlay.classList.add("hidden"));
    cartDrawerItemElements.forEach((overlay) =>
      overlay.classList.add("hidden")
    );
  }
}
customElements.define("cart-items", CartItems);

class CartDrawerItems extends CartItems {
  constructor() {
    super();
  }
}
customElements.define("cart-drawer-items", CartDrawerItems);

class CartRemoveButton extends HTMLElement {
  constructor() {
    super();
    this.addEventListener("click", (event) => {
      event.preventDefault();
      const cartItems =
        this.closest("cart-items") || this.closest("cart-drawer-items");
      cartItems.updateLineItem({
        line: this.dataset.index,
        quantity: 0,
        name: null,
        eventTarget: event.target,
      });
    });
  }
}
customElements.define("cart-remove", CartRemoveButton);

class CartSellingPlanRadios extends HTMLElement {
  constructor() {
    super();

    this.autoReplenish = this.querySelector(
      'input[type="checkbox"][data-auto-replenish]'
    );
    this.sellingPlanSelect = this.querySelector("[data-selling-plan-select]");

    this.autoReplenish?.addEventListener(
      "click",
      this.onAutoReplenishChange.bind(this)
    );

    this.addEventListener("focusout", this.onFocusOut.bind(this));
  }

  onAutoReplenishChange(event) {
    if (event.target.checked) {
      this.sellingPlanSelect.classList.remove("hidden");
    } else {
      this.sellingPlanSelect.classList.add("hidden");
    }
  }
  onFocusOut(event) {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) {
        if (!this.autoReplenish) return;
        this.autoReplenish.checked = false;
        this.sellingPlanSelect.classList.add("hidden");
      }
    }, 100);
  }
}
customElements.define("cart-selling-plan-radios", CartSellingPlanRadios);

class CartNote extends HTMLElement {
  constructor() {
    super();

    this.addEventListener(
      "change",
      debounce((event) => {
        const body = JSON.stringify({ note: event.target.value });
        fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
      }, ON_CHANGE_DEBOUNCE_TIMER)
    );
  }
}
customElements.define("cart-note", CartNote);

class RecipientForm extends HTMLElement {
  constructor() {
    super();
    this.recipientFieldsLiveRegion = this.querySelector(
      `#Recipient-fields-live-region-${this.dataset.sectionId}`
    );
    this.checkboxInput = this.querySelector(
      `#Recipient-checkbox-${this.dataset.sectionId}`
    );
    this.checkboxInput.disabled = false;
    this.hiddenControlField = this.querySelector(
      `#Recipient-control-${this.dataset.sectionId}`
    );
    this.hiddenControlField.disabled = true;
    this.recipientFields = this.querySelector(
      `#Recipient-fields-${this.dataset.sectionId}`
    );
    this.emailInput = this.querySelector(
      `#Recipient-email-${this.dataset.sectionId}`
    );
    this.nameInput = this.querySelector(
      `#Recipient-name-${this.dataset.sectionId}`
    );
    this.messageInput = this.querySelector(
      `#Recipient-message-${this.dataset.sectionId}`
    );
    this.sendonInput = this.querySelector(
      `#Recipient-send-on-${this.dataset.sectionId}`
    );
    this.offsetProperty = this.querySelector(
      `#Recipient-timezone-offset-${this.dataset.sectionId}`
    );
    if (this.offsetProperty)
      this.offsetProperty.value = new Date().getTimezoneOffset().toString();

    this.errorMessageWrapper = this.querySelector(
      ".product-form__recipient-error-message-wrapper"
    );
    this.errorMessageList = this.errorMessageWrapper?.querySelector("ul");
    this.errorMessage =
      this.errorMessageWrapper?.querySelector(".error-message");
    this.defaultErrorHeader = this.errorMessage?.innerText;
    this.currentProductVariantId = this.dataset.productVariantId;
    this.addEventListener("change", this.onChange.bind(this));
    this.onChange();
  }

  cartUpdateUnsubscriber = undefined;
  variantChangeUnsubscriber = undefined;
  cartErrorUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(
      PUB_SUB_EVENTS.cartUpdate,
      (event) => {
        if (
          event.source === "product-form" &&
          event.productVariantId.toString() === this.currentProductVariantId
        ) {
          this.resetRecipientForm();
          this.onChange();
        }
      }
    );

    this.variantChangeUnsubscriber = subscribe(
      PUB_SUB_EVENTS.variantChange,
      (event) => {
        if (event.data.sectionId === this.dataset.sectionId) {
          this.currentProductVariantId = event.data.variant.id.toString();
        }
      }
    );

    this.cartUpdateUnsubscriber = subscribe(
      PUB_SUB_EVENTS.cartError,
      (event) => {
        if (
          event.source === "product-form" &&
          event.productVariantId.toString() === this.currentProductVariantId
        ) {
          this.displayErrorMessage(event.message, event.errors);
        }
      }
    );
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }

    if (this.variantChangeUnsubscriber) {
      this.variantChangeUnsubscriber();
    }

    if (this.cartErrorUnsubscriber) {
      this.cartErrorUnsubscriber();
    }
  }

  onChange() {
    if (this.checkboxInput.checked) {
      this.showInputFields();
      this.enableInputFields();
      this.recipientFieldsLiveRegion.innerText =
        window.accessibilityStrings.recipientFormExpanded;
    } else {
      this.hideInputFields();
      this.clearInputFields();
      this.disableInputFields();
      this.clearErrorMessage();
      this.recipientFieldsLiveRegion.innerText =
        window.accessibilityStrings.recipientFormCollapsed;
    }
  }

  inputFields() {
    return [
      this.emailInput,
      this.nameInput,
      this.messageInput,
      this.sendonInput,
    ];
  }

  hideInputFields() {
    this.recipientFields.classList.add("hidden");
  }

  showInputFields() {
    this.recipientFields.classList.remove("hidden");
  }

  disableableFields() {
    return [...this.inputFields(), this.offsetProperty];
  }

  clearInputFields() {
    this.inputFields().forEach((field) => (field.value = ""));
  }

  enableInputFields() {
    this.disableableFields().forEach((field) => (field.disabled = false));
  }

  disableInputFields() {
    this.disableableFields().forEach((field) => (field.disabled = true));
  }

  displayErrorMessage(title, body) {
    this.clearErrorMessage();
    this.errorMessageWrapper.hidden = false;
    if (typeof body === "object") {
      this.errorMessage.innerText = this.defaultErrorHeader;
      return Object.entries(body).forEach(([key, value]) => {
        const errorMessageId = `RecipientForm-${key}-error-${this.dataset.sectionId}`;
        const fieldSelector = `#Recipient-${key}-${this.dataset.sectionId}`;
        const message = `${value.join(", ")}`;
        const errorMessageElement = this.querySelector(`#${errorMessageId}`);
        const errorTextElement =
          errorMessageElement?.querySelector(".error-message");
        if (!errorTextElement) return;

        if (this.errorMessageList) {
          this.errorMessageList.appendChild(
            this.createErrorListItem(fieldSelector, message)
          );
        }

        errorTextElement.innerText = `${message}.`;
        errorMessageElement.classList.remove("hidden");

        const inputElement = this[`${key}Input`];
        if (!inputElement) return;

        inputElement.setAttribute("aria-invalid", true);
        inputElement.setAttribute("aria-describedby", errorMessageId);
      });
    }

    this.errorMessage.innerText = body;
  }

  createErrorListItem(target, message) {
    const li = document.createElement("li");
    const a = document.createElement("a");
    a.setAttribute("href", target);
    a.innerText = message;
    li.appendChild(a);
    li.className = "error-message";
    return li;
  }

  clearErrorMessage() {
    this.errorMessageWrapper.hidden = true;

    if (this.errorMessageList) this.errorMessageList.innerHTML = "";

    this.querySelectorAll(".recipient-fields .form__message").forEach(
      (field) => {
        field.classList.add("hidden");
        const textField = field.querySelector(".error-message");
        if (textField) textField.innerText = "";
      }
    );

    [
      this.emailInput,
      this.messageInput,
      this.nameInput,
      this.sendonInput,
    ].forEach((inputElement) => {
      inputElement.setAttribute("aria-invalid", false);
      inputElement.removeAttribute("aria-describedby");
    });
  }

  resetRecipientForm() {
    if (this.checkboxInput.checked) {
      this.checkboxInput.checked = false;
      this.clearInputFields();
      this.clearErrorMessage();
    }
  }
}

customElements.define("recipient-form", RecipientForm);

class SellingPlansWidget {
  constructor(sellingPlansWidgetContainer) {
    this.enablePerformanceObserver();
    this.sellingPlansWidgetContainer = sellingPlansWidgetContainer;
    //this.appendSellingPlanInputs();
    this.updateSellingPlanInputsValues();
    this.listenToVariantChange();
    this.listenToSellingPlanFormRadioButtonChange();
    // this.updatePrice();
  }

  get sectionId() {
    return this.sellingPlansWidgetContainer.getAttribute("data-section-id");
  }

  get shopifySection() {
    return document.querySelector(`#shopify-section-${this.sectionId}`);
  }

  /*
    We are careful to target the correct form, as there are instances when we encounter an installment form that we specifically aim to avoid interacting with.
  */
  get variantIdInput() {
    return (
      this.addToCartForms[1]?.querySelector(`input[name="id"]`) ||
      this.addToCartForms[1]?.querySelector(`select[name="id"]`) ||
      this.addToCartForms[0].querySelector(`input[name="id"]`) ||
      this.addToCartForms[0].querySelector(`select[name="id"]`)
    );
  }

  get priceElement() {
    return this.shopifySection.querySelector(".price");
  }

  get comparedAtPrice() {
    return this.shopifySection.querySelector(".price__sale");
  }

  get visibleSellingPlanForm() {
    return this.shopifySection.querySelector(
      `section[data-variant-id^="${this.variantIdInput.value}"]`
    );
  }

  get isVariantAvailable() {
    return this.selectedPurchaseOption.getAttributeNames().includes("disabled");
  }

  get sellingPlanInput() {
    return this.shopifySection.querySelector(".selected-selling-plan-id");
  }

  get addToCartForms() {
    return this.shopifySection.querySelectorAll('[action*="/cart/add"]');
  }

  /*
    To enable the addition of a selling plan to a cart, it's necessary to include an input with the name "selling_plan", which will carry the selling ID as its value. When a buyer clicks on 'add to cart', the appropriate selling plan ID is added to their cart.
  */
  appendSellingPlanInputs() {
    this.addToCartForms.forEach((addToCartForm) => {
      addToCartForm.appendChild(this.sellingPlanInput.cloneNode());
    });
  }

  showSellingPlanForm(sellingPlanFormForSelectedVariant) {
    sellingPlanFormForSelectedVariant?.classList?.remove(
      "selling_plan_theme_integration--hidden"
    );
  }

  hideSellingPlanForms(sellingPlanFormsForUnselectedVariants) {
    sellingPlanFormsForUnselectedVariants.forEach((element) => {
      element.classList.add("selling_plan_theme_integration--hidden");
    });
  }

  resetSellingPlanForms(sellingPlanFormsForUnselectedVariants) {
    sellingPlanFormsForUnselectedVariants.forEach((element) => {
      element
        .querySelector('input[type="radio"][data-one-time-purchase]')
        .click();
    });
  }

  /*
    Each product variant comes with a selling plan selection box that the buyer can interact with.
    When a buyer chooses a different variant, we ensure that only the relevant selling plan selection box is displayed.
    This guarantees that only the selling plan associated with the selected variant is shown.
  */
  handleSellingPlanFormVisibility() {
    const sellingPlanFormForSelectedVariant = this.shopifySection.querySelector(
      `section[data-variant-id="${this.variantIdInput.value}"]`
    );
    const sellingPlanFormsForUnselectedVariants =
      this.shopifySection.querySelectorAll(
        `.selling_plan_theme_integration:not([data-variant-id="${this.variantIdInput.value}"])`
      );
    this.showSellingPlanForm(sellingPlanFormForSelectedVariant);
    this.hideSellingPlanForms(sellingPlanFormsForUnselectedVariants);
    this.resetSellingPlanForms(sellingPlanFormsForUnselectedVariants);
  }

  handleVariantChange() {
    this.handleSellingPlanFormVisibility();
    // reset variant
    this.updateSellingPlanInputsValues();
    //this.listenToSellingPlanFormRadioButtonChange();
  }

  /*
    The functions listenToVariantChange() and listenToAddToCartForms() are implemented to track when a product variant is altered or when the product form is updated.
    The identification of the variant is crucial as it dictates which selling plan box should be displayed.
  */
  listenToVariantChange() {
    this.listenToAddToCartForms();
    if (this.variantIdInput.tagName === "INPUT") {
      const variantIdObserver = new MutationObserver((mutationList) => {
        mutationList.forEach((mutationRecord) => {
          this.handleVariantChange(mutationRecord.target.value);
        });
      });

      variantIdObserver.observe(this.variantIdInput, {
        attributes: true,
      });
    }
  }

  listenToAddToCartForms() {
    this.addToCartForms.forEach((addToCartForm) => {
      addToCartForm.addEventListener("change", () => {
        this.handleVariantChange();
      });
    });
  }

  get regularPriceElement() {
    return this.shopifySection.querySelector(".price__regular");
  }

  get salePriceElement() {
    return this.shopifySection.querySelector(".price__sale");
  }

  get salePriceValue() {
    return this.salePriceElement.querySelector(".price-item--sale");
  }

  get regularPriceValue() {
    return this.salePriceElement.querySelector(".price-item--regular");
  }

  get sellingPlanAllocationPrice() {
    return document.getElementById(
      `${this.selectedPurchaseOption.dataset.sellingPlanGroupId}_allocation_price`
    );
  }

  get selectedPurchaseOptionPrice() {
    return this.selectedPurchaseOption.dataset.variantPrice;
  }

  get selectedPurchaseOptionComparedAtPrice() {
    return this.selectedPurchaseOption.dataset.variantCompareAtPrice;
  }

  get price() {
    return this.sellingPlanAllocationPrices.price ?? null;
  }

  /*
    We aim to ascertain whether a compared price exists, which would indicate that the currently selected input has a discount applied to it.
    If a discount is detected, the discounted price is displayed; otherwise, the regular price is shown.
  */
  updatePrice() {
    if (
      !this.selectedPurchaseOptionComparedAtPrice ||
      this.selectedPurchaseOptionComparedAtPrice ===
        this.selectedPurchaseOptionPrice
    ) {
      this.showRegularPrice();
      this.hideSalePrice();
      this.priceElement.classList.remove("price--on-sale");
    } else {
      this.showSalePrice();
      this.hideRegularPrice();
      this.priceElement.classList.add("price--on-sale");
    }
  }

  hideSalePrice() {
    this.salePriceElement.style.display = "none";
  }

  hideRegularPrice() {
    this.regularPriceElement.style.display = "none";
  }

  showRegularPrice() {
    this.regularPriceElement.style.display = "block";
    this.shopifySection.querySelector(".price__sale").style.display = "none";
  }

  showSalePrice() {
    this.salePriceElement.style.display = "block";
    this.regularPriceValue.innerHTML =
      this.selectedPurchaseOptionComparedAtPrice;
    this.salePriceValue.innerHTML = this.selectedPurchaseOptionPrice;
  }

  get sellingPlanInputs() {
    return this.shopifySection.querySelectorAll(".selected-selling-plan-id");
  }

  updateSellingPlanInputsValues() {
    this.sellingPlanInputs.forEach((sellingPlanInput) => {
      sellingPlanInput.value = this.sellingPlanInputValue;
    });
  }

  get sellingPlanInputValue() {
    return this.selectedPurchaseOption?.dataset.sellingPlanId ?? null;
  }

  get selectedPurchaseOption() {
    return this.visibleSellingPlanForm?.querySelector(
      'input[type="radio"][data-purchase-option]:checked'
    );
  }

  set selectedPurchaseOption(selectedPurchaseOption) {
    this._selectedPurchaseOption = selectedPurchaseOption;
  }

  handleRadioButtonChange(selectedPurchaseOption) {
    this.selectedPurchaseOption = selectedPurchaseOption;
    this.updateSellingPlanInputsValues();
    // this.updatePrice();
  }

  handleAutoReplenishChange(selectedPurchaseOption) {
    selectedPurchaseOption.click();
  }

  listenToSellingPlanFormRadioButtonChange() {
    this.sellingPlansWidgetContainer
      ?.querySelectorAll('input[type="radio"][data-purchase-option]')
      .forEach((radio) => {
        radio.addEventListener("change", (event) => {
          this.handleRadioButtonChange(event.target);
        });
      });

    this.sellingPlansWidgetContainer
      ?.querySelectorAll('input[type="radio"][data-selling-plan]')
      .forEach((radio) => {
        radio.addEventListener("change", (event) => {
          const auto = event.target
            .closest(".selling_plan_theme_integration")
            .querySelector('input[type="radio"][data-auto-replenish]');
          if (event.target.checked && !auto.checked) {
            auto.checked = true;
          }
        });
      });

    this.sellingPlansWidgetContainer
      ?.querySelectorAll('input[type="radio"][data-one-time-purchase]')
      .forEach((radio) => {
        radio.addEventListener("change", (event) => {
          if (event.target.checked) {
            const select = event.target
              .closest(".selling_plan_theme_integration")
              .querySelector(".product-selling-plan-select");
            select.classList.add("hidden");
            const radio = event.target
              .closest(".selling_plan_theme_integration")
              .querySelector('input[type="radio"][data-selling-plan]:checked');
            radio.checked = false;
          }
        });
      });

    this.sellingPlansWidgetContainer
      ?.querySelectorAll('input[type="radio"][data-auto-replenish]')
      .forEach((radio) => {
        radio.addEventListener("change", (event) => {
          if (event.target.checked) {
            const select = event.target
              .closest(".selling_plan_theme_integration")
              .querySelector(".product-selling-plan-select");
            select.classList.remove("hidden");
            const radio = event.target
              .closest(".selling_plan_theme_integration")
              .querySelector(
                'input[type="radio"][data-first-available-selling-plan]'
              );
            this.handleAutoReplenishChange(radio);
          }
        });
      });
  }

  enablePerformanceObserver() {
    const performanceObserver = new PerformanceObserver((list) => {
      list.getEntries().forEach((entry) => {
        if (entry.initiatorType !== "fetch") return;

        const url = new URL(entry.name);
        /*
          When a buyer selects a product variant, a fetch request is initiated.
          Upon completion of this fetch request, we update the price to reflect the correct value.
        */
        if (url.search.includes("variant") || url.search.includes("variants")) {
          //this.updatePrice();
        }
      });
    });

    performanceObserver.observe({ entryTypes: ["resource"] });
  }
}

document
  .querySelectorAll(".selling_plan_app_container")
  .forEach((sellingPlansWidgetContainer) => {
    new SellingPlansWidget(sellingPlansWidgetContainer);
  });

// Remove GWP item from cart if no discount is applied
//   window.gwp.getGWPCart().then((cart) => {

//     if (window.gwp.isGWPItemYInCart(cart)) {

//       function getSectionsToRender() {
//         return [
//           {
//             id: "CartDrawer",
//             section: "cart-drawer",
//             selector: ".cart-drawer-content",
//           },
//           {
//             id: "cart-bubble",
//             section: "cart-bubble",
//             selector: ".shopify-section",
//           },
//           {
//             id: "Cart",
//             section: "cart",
//             selector: ".cart-content",
//           },
//         ];
//       }

//       window.gwp.removeGWPfromCart(cart, getSectionsToRender().map((section) => section.section), context = "load");
//     }
// });
